import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Grid,
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Checkbox,
  FormGroup,
  Tooltip,
  MenuItem,
  Select,
  Chip,
  Breadcrumbs
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import Button from "../../../components/Button";
import GridTable from "../../../components/GridTable";
import TextField from "../../../components/TextField";
import AlertDialogModal from "../../../components/Dialog";
import ContentCopyRounded from "@mui/icons-material/ContentCopyRounded";
import Dropdown from "../../../components/Dropdown";
import Loader from "../../../components/Loader";
import { successMessage, errorMessage } from "../../../components/Toaster";
import ActionMenu from "../../../components/ActionMenu";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import EditIcon from "@mui/icons-material/Edit";
import PermDataSettingIcon from "@mui/icons-material/PermDataSetting";
import DeleteIcon from "@mui/icons-material/Delete";
import { GridCellParams } from "@mui/x-data-grid";
import { getCropConfigById, createCropConfig, updateCropConfigById } from "./api";
import { ClassData } from "../../class/interface";
import { getAllClasses } from "../../class/api";
import {
  ApiResponse,
  CropConfig,
  CreateCropConfigParams,
  UpdateCropConfigParams,
  RequestConfig,
} from "./interface";
import { getErrorMessage } from "../../../utils/error";
import { getAllMachines } from "../../machine/api";
import { getVarietiesByCropId } from '../variety/api'
import { MachineData } from "../../machine/interface";
import { VarietyData } from '../variety/interface'
const rangeTypeOptions = ["Select By %", "Weight", "Count"];
const enamTypeValues = ["DEFAULT_UPJAO", "DEFAULT_ENAM"]
const processImageOptions = [
  "classes",
  "bboxes",
  "encodings",
  "encoding_colors",
  "bboxes_bottom",
];
const kernelDetailsOptions = [
  "classes",
  "total_kernel_count",
  "total_kernel_weight",
  "consider_area",
  "consider_length",
  "kernel_counts",
  "kernel_weights",
  "weight_thresholds",
  "quality_score_dependencies",
  "overall_score",
  "areas",
  "kernel_lengths",
  "range_distributions",
];
const kernelDistributionsOptions = ["kernel_dist"];
const distAvgIndexesOptions = ["kernel_avg_dist"];
const alertsOptions = ["alerts"];
const croppedKernelDetailsOptions = [
  "classes",
  "bboxes",
  "beautify",
  "bboxes_bottom",
];
const distributionAvgValuesOptions = [
  "kernel_avg_area",
  "kernel_avg_height",
  "kernel_avg_length",
  "kernel_avg_width",
  "whiteness",
  "kernel_avg_length_no_foreign",
  "length / width ratio",
  "total_corn_weight"
];
const distributionKeysOptions = ["total_area", "length", "width", "height"];
const ConfigDetails: React.FC = () => {
  const { crop_id, crop_name, variety_name, machine_name, action, crop_config_id } = useParams<{
    crop_id: string;
    crop_name: string;
    variety_name: string;
    machine_name: string;
    action: string;
    crop_config_id: string;
  }>();
  const [classes, setClasses] = useState<ClassData[]>([]);
  const [machines, setMachines] = useState<MachineData[]>([]);
  const [varieties, setVarieties] = useState<VarietyData[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [editRequestConfig, setEditRequestConfig] = useState<{
    sr_no: number;
    display_name: string;
    values: string[];
  }>({
    sr_no: 0,
    display_name: "",
    values: [],
  });
  const [editMode, setEditMode] = useState(false);
  const [tableData, setTableData] = useState<RequestConfig[]>([]);
  const [editConfig, setEditConfig] = useState<CropConfig | undefined>(undefined);
  const [loading, setLoading] = useState({
    update: false,
    delete: false,
    create: false,
    fetch: false,
  });

  const beautify = {
    translations: {
      display_order: {} as Record<string, string>,
      class_code: {} as Record<string, string>,
      class_color: {} as Record<string, string>,
    },
  };

  const req_columns = [
    {
      field: "display_name",
      headerName: "Display Name",
      headerClassName: "table-header",
      disableColumnMenu: true,
      flex: 1,
    },
    {
      field: "key_name",
      headerName: "Key Name",
      headerClassName: "table-header",
      disableColumnMenu: true,
      flex: 1,
    },
    {
      field: "values",
      headerName: "Values",
      headerClassName: "table-header",
      disableColumnMenu: true,
      flex: 1,
    },
    {
      field: "actions",
      headerName: "Actions",
      headerClassName: "table-header",
      renderCell: (params: GridCellParams) => (
        <ActionMenu
          params={params}
          actions={[
            {
              icon: <EditIcon color="primary" />,
              tooltip: "Edit",
              onClick: handleEdit,
            },
            {
              icon: <DeleteIcon color="error" />,
              tooltip: "Delete",
              onClick: handleDelete,
            },
          ]}
        />
      ),
      sortable: false,
      filterable: false,
      flex: 1,
    },
  ];

  const validationSchema = Yup.object().shape({
    varietyId: Yup.lazy(() =>
      action === "edit"
        ? Yup.string().nullable()
        : Yup.string().required("Variety is required")
    ),
    machineId: Yup.lazy(() =>
      action === "edit"
        ? Yup.string().nullable()
        : Yup.string().required("Machine is required")
    ),
    type: Yup.lazy(() =>
      action === "edit"
        ? Yup.string().nullable()
        : Yup.string()
          .oneOf(enamTypeValues, "Invalid response type")
          .required("Response type is required")
    ),
    pipeline_id: Yup.string().required(
      "Pipeline ID is required"
    ),
    contactMethod: Yup.string()
      .required("Config Methods are required")
      .oneOf(
        [
          "request_config",
          "crop_class_config",
          "crop_instruction_config",
          "response_config",
        ],
        "Invalid contact method"
      ),

    // Conditionally required only when contactMethod is "crop_instruction_config"
    min_threshold: Yup.number().when("contactMethod", {
      is: "crop_instruction_config",
      then: (schema) => schema.required("Min Threshold is required"),
      otherwise: (schema) => schema.notRequired(),
    }),

    mid_threshold: Yup.number().when("contactMethod", {
      is: "crop_instruction_config",
      then: (schema) => schema.required("Mid Threshold is required"),
      otherwise: (schema) => schema.notRequired(),
    }),

    max_threshold: Yup.number().when("contactMethod", {
      is: "crop_instruction_config",
      then: (schema) => schema.required("Max Threshold is required"),
      otherwise: (schema) => schema.notRequired(),
    }),

    paper_type: Yup.string().when("contactMethod", {
      is: "crop_instruction_config",
      then: (schema) => schema.required("Paper Type is required"),
      otherwise: (schema) => schema.notRequired(),
    }),

    // Conditionally required when contactMethod is "response_config"
    processed_image: Yup.array()
      .of(Yup.string())
      .when("contactMethod", {
        is: "response_config",
        then: (schema) =>
          schema
            .min(1, "Process Image must have at least one item")
            .required("Process Image is required"),
        otherwise: (schema) => schema.notRequired(),
      }),

    kernel_details: Yup.array()
      .of(Yup.string())
      .when("contactMethod", {
        is: "response_config",
        then: (schema) =>
          schema
            .min(1, "Kernel Details must have at least one item")
            .required("Kernel Details are required"),
        otherwise: (schema) => schema.notRequired(),
      }),

    kernel_distributions: Yup.array()
      .of(Yup.string())
      .when("contactMethod", {
        is: "response_config",
        then: (schema) =>
          schema
            .min(1, "Kernel Distributions must have at least one item")
            .required("Kernel Distributions are required"),
        otherwise: (schema) => schema.notRequired(),
      }),

    dist_avg_indexes: Yup.array()
      .of(Yup.string())
      .when("contactMethod", {
        is: "response_config",
        then: (schema) =>
          schema
            .min(1, "Dist Avg Indexes must have at least one item")
            .required("Dist Avg Indexes are required"),
        otherwise: (schema) => schema.notRequired(),
      }),

    alerts: Yup.array()
      .of(Yup.string())
      .when("contactMethod", {
        is: "response_config",
        then: (schema) =>
          schema
            .min(1, "Alerts must have at least one item")
            .required("Alerts are required"),
        otherwise: (schema) => schema.notRequired(),
      }),

    cropped_kernel_details: Yup.array()
      .of(Yup.string())
      .when("contactMethod", {
        is: "response_config",
        then: (schema) =>
          schema
            .min(1, "Cropped Kernel Details must have at least one item")
            .required("Cropped Kernel Details are required"),
        otherwise: (schema) => schema.notRequired(),
      }),

    // Conditionally required when contactMethod is "crop_class_config"
    distribution_avg_values: Yup.array()
      .of(Yup.string().required("Distribution Avg Value is required"))
      .when("contactMethod", {
        is: "crop_class_config",
        then: (schema) =>
          schema
            .min(1, "At least one Distribution Avg Value must be selected")
            .required("Distribution Avg Values are required"),
        otherwise: (schema) => schema.notRequired(),
      }),

    distribution_key: Yup.array()
      .of(Yup.string().required("Distribution Key is required"))
      .when("contactMethod", {
        is: "crop_class_config",
        then: (schema) =>
          schema
            .min(1, "At least one Distribution Key must be selected")
            .required("Distribution Key is required"),
        otherwise: (schema) => schema.notRequired(),
      }),

    class_order: Yup.array()
      .of(Yup.string().required("Class Order is required"))
      .when("contactMethod", {
        is: "crop_class_config",
        then: (schema) =>
          schema
            .min(1, "At least one Class Order must be selected")
            .required("Class Order is required"),
        otherwise: (schema) => schema.notRequired(),
      }),

    display_order: Yup.array()
      .of(Yup.string().required("Display Order is required"))
      .when("contactMethod", {
        is: "crop_class_config",
        then: (schema) =>
          schema
            .min(1, "At least one Display Order must be selected")
            .required("Display Order is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
  });

  useEffect(() => {
    if (crop_config_id && action === "edit") {
      fetchCropConfig(crop_config_id);
    }
  }, [crop_config_id, action]);

  useEffect(() => {
    if (editConfig?.request_config) {
      const withSrNo = editConfig.request_config.map((item, index) => ({
        ...item,
        sr_no: index + 1,
      }));
      setTableData(withSrNo);
    }
  }, [editConfig]);


  const fetchCropConfig = async (crop_config_id: string) => {
    setLoading((prev) => ({ ...prev, fetch: true }));
    try {
      const response: ApiResponse<CropConfig> = await getCropConfigById(crop_config_id);
      if (Array.isArray(response.data)) {
        if (response.status && response.data) {
          setEditConfig(response.data);
        } else {
          setEditConfig(undefined);
        }
      } else if (response.data) {
        setEditConfig(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch crop configuration:", error);
    }
    setLoading((prev) => ({ ...prev, fetch: false }));
  };

  useEffect(() => {
    if (crop_id) {
      fetchVarietiesAndMachines(crop_id);
      fetchClassesData();
    }
  }, [crop_id]);

  const fetchClassesData = async () => {
    setLoading((prev) => ({ ...prev, fetch: true }));
    try {
      const res: ApiResponse<ClassData[]> = await getAllClasses();
      if (res.status && res.data) {
        setClasses(res.data);
      } else {
        errorMessage(res.message || "Failed to fetch classes.");
      }
    } catch (error) {
      console.error("Failed to fetch classes:", error);
    }
    setLoading((prev) => ({ ...prev, fetch: false }));
  };

  const fetchVarietiesAndMachines = async (crop_id: string) => {
    setLoading((prev) => ({ ...prev, fetch: true }));
    try {
      const varietiesRes = await getVarietiesByCropId(crop_id);
      if (varietiesRes.status && varietiesRes.data) {
        setVarieties(varietiesRes.data);
      } else {
        errorMessage(varietiesRes.message || "Failed to fetch varieties.");
      }

      const machinesRes: ApiResponse<MachineData[]> = await getAllMachines();
      if (machinesRes.status && machinesRes.data) {
        setMachines(machinesRes.data);
      } else {
        errorMessage(machinesRes.message || "Failed to fetch machines.");
      }

    } catch (error) {
      console.error("Failed to fetch varieties or machines:", error);
      errorMessage("An unexpected error occurred while fetching data.");
    }

    setLoading((prev) => ({ ...prev, fetch: false }));
  };

  const handleDelete = (params: GridCellParams) => {
    if (Array.isArray(tableData)) {
      const updatedTableData = tableData.filter(
        (item: RequestConfig) => item.key_name !== params.row.key_name
      );
      setTableData(updatedTableData);
      successMessage("Successfully deleted the request config")
    }
  };

  const handleEdit = (params: GridCellParams) => {
    setEditRequestConfig(params.row);
    setEditMode(true);
    openDialog();
  };

  const openDialog = () => setDialogOpen(true);
  const closeDialog = () => {
    setDialogOpen(false);
    setEditMode(false);
    setEditRequestConfig({
      sr_no: 0,
      display_name: "",
      values: [],
    });
    setInputValue("");
  };

  const handleUpdate = async (values: any) => {
    setLoading((prev) => ({ ...prev, update: true }));
    const filteredMappings: Record<string, string> = Object.fromEntries(
      Object.entries(values.class_mappings || {}).filter(
        ([, value]) =>
          typeof value === "string" &&
          values.display_order.includes(value)
      )
    ) as Record<string, string>;

    let filteredGrading = values.grading || {};
    if (values.type === "DEFAULT_ENAM") {
      filteredGrading = Object.fromEntries(
        Object.entries(filteredGrading).filter(([key]) =>
          key === "moisture" || values.display_order.includes(key)
        )
      );
    }

    if (values.display_order && Array.isArray(values.display_order)) {
      values.display_order.forEach((classId: string) => {
        const selectedClass = classes.find((item) => item.class_name === classId);
        if (selectedClass) {
          beautify.translations.class_code[selectedClass.class_name] = selectedClass.class_code;
          beautify.translations.class_color[selectedClass.class_name] = selectedClass.color_code;
          beautify.translations.display_order[selectedClass.class_name] = selectedClass.class_translation.EN;
        }
      });
    }
    const updatedData: UpdateCropConfigParams = {
      crop_config_id: editConfig!.crop_config_id,
      response_config: {
        pipeline_id: values.pipeline_id,
        processed_image: values.processed_image,
        kernel_details: values.kernel_details,
        kernel_distributions: values.kernel_distributions,
        dist_avg_indexes: values.dist_avg_indexes,
        alerts: values.alerts,
        cropped_kernel_details: values.cropped_kernel_details,
      },
      crop_instruction_config: {
        min_threshold: values.min_threshold,
        mid_threshold: values.mid_threshold,
        max_threshold: values.max_threshold,
        paper_type: values.paper_type,
      },
      crop_class_config: {
        pipeline_id: values.pipeline_id,
        beautify: beautify,
        consider_area: values.consider_area,
        consider_length: values.consider_length,
        consider_width: values.consider_width,
        show_segmented_image: values.show_segmented_image,
        distribution_avg_values: values.distribution_avg_values,
        distribution_key: values.distribution_key,
        class_order: values.class_order,
        display_order: values.display_order,
        class_mappings: filteredMappings,
        quality_score_dependencies: values.quality_score_dependencies,
        ...(values.type === "DEFAULT_ENAM" && { grading: filteredGrading }),
      },
      request_config: tableData,
    };
    try {
      const response = await updateCropConfigById(updatedData);
      if (response.status) {
        successMessage(response.message);
      }
    } catch (error) {
      console.error("Error updating crop config:", error);
      errorMessage(getErrorMessage(error) || 'Error updating crop config:');
    }
    setLoading((prev) => ({ ...prev, update: false }));
  };

  const handleCreate = async (values: any) => {
    setLoading((prev) => ({ ...prev, create: true }));

    if (values.display_order && Array.isArray(values.display_order)) {
      values.display_order.forEach((classId: string) => {
        const selectedClass = classes.find((item) => item.class_name === classId);
        if (selectedClass) {
          beautify.translations.class_code[selectedClass.class_name] = selectedClass.class_code;
          beautify.translations.class_color[selectedClass.class_name] = selectedClass.color_code;
          beautify.translations.display_order[selectedClass.class_name] = selectedClass.class_translation.EN;
        }
      });
    }

    const newVarietyData: CreateCropConfigParams = {
      crop_id: crop_id ? crop_id : "",
      variety_id: values.varietyId,
      machine_id: values.machineId,
      type: values.type,
      response_config: {
        pipeline_id: values.pipeline_id,
        processed_image: values.processed_image,
        kernel_details: values.kernel_details,
        kernel_distributions: values.kernel_distributions,
        dist_avg_indexes: values.dist_avg_indexes,
        alerts: values.alerts,
        cropped_kernel_details: values.cropped_kernel_details,
      },
      crop_instruction_config: {
        min_threshold: values.min_threshold,
        mid_threshold: values.mid_threshold,
        max_threshold: values.max_threshold,
        paper_type: values.paper_type,
      },
      crop_class_config: {
        pipeline_id: values.pipeline_id,
        beautify: beautify,
        consider_area: values.consider_area,
        consider_length: values.consider_length,
        consider_width: values.consider_width,
        show_segmented_image: values.show_segmented_image,
        distribution_avg_values: values.distribution_avg_values,
        distribution_key: values.distribution_key,
        class_order: values.class_order,
        display_order: values.display_order,
        class_mappings: values.class_mappings,
        quality_score_dependencies: values.quality_score_dependencies,
        ...(values.type === "DEFAULT_ENAM" && { grading: values.grading }),
      },
      request_config: tableData,
    };
    try {
      const response = await createCropConfig(newVarietyData);
      if (response.status) {
        successMessage(response.message);
      }
    } catch (error) {
      console.error("Error creating crop config:", error);
      errorMessage(getErrorMessage(error) || 'Error creating crop config');
    }
    setLoading((prev) => ({ ...prev, create: false }));
  };

  const handlePasteClick = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const copiedValues = JSON.parse(text);
      setEditConfig(copiedValues);
      successMessage("Successfully pasted the data");
    } catch (err) {
      console.error("Failed to paste: ", err);
      errorMessage("Failed to paste the data");
    }
  };

  const handleReqConfig = async () => {
    // Function to generate key_name from display_name
    const generateKeyName = (displayName: string) => {
      return displayName.trim().toLowerCase().replace(/\s+/g, "_");
    };

    const isDisplayNameExists = (editConfig?.request_config || []).some(
      (config: RequestConfig) =>
        config.display_name === editRequestConfig.display_name
    );

    if (!editMode && isDisplayNameExists) {
      errorMessage("Display name already exists")
      return;
    }

    if (editMode) {
      // Update the tableData with the updated config (without changing editConfig)
      const updatedTableData = (tableData || []).map((config: RequestConfig) => {
        return config.sr_no === editRequestConfig.sr_no
          ? {
            ...editRequestConfig,
            key_name: generateKeyName(editRequestConfig.display_name),
          }
          : config;
      });
      setTableData(updatedTableData);
      successMessage("Successfully updated the request config.");
    } else {
      // If adding a new request config, add it only to tableData (not editConfig)
      const newSrNo = (tableData?.length || 0) + 1;
      const newConfig: RequestConfig = {
        sr_no: newSrNo,
        display_name: editRequestConfig.display_name,
        key_name: generateKeyName(editRequestConfig.display_name),
        values: editRequestConfig.values,
      };
      const updatedTableData = [...(tableData || []), newConfig];
      setTableData(updatedTableData);
      successMessage("Successfully added the request config.");
    }

    closeDialog();
  };

  const confirmDialog = async (
    values: any,
    { resetForm }: { resetForm: () => void }
  ) => {
    if (action === "edit") {
      await handleUpdate(values);
    } else {
      await handleCreate(values);
      setEditConfig(undefined);
      resetForm();
    }
  };

  const handleInputChange = (field: string, value: string | string[]) => {
    setEditRequestConfig((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDeleteValue = (valueToDelete: string) => {
    handleInputChange(
      "values",
      editRequestConfig.values.filter((value) => value !== valueToDelete)
    );
  };

  const handleAddValue = () => {
    if (inputValue.trim()) {
      if (editRequestConfig.values.includes(inputValue.trim())) {
        errorMessage("This value has already been added!");
      } else {
        handleInputChange("values", [
          ...editRequestConfig.values,
          inputValue.trim(),
        ]);
      }
      setInputValue("");
    }
  };

  return (
    <Box>
      <main className="m-a-20">
        <div
          style={{ display: "flex", alignItems: "center", marginBottom: "30px" }}
        >
          {action === "edit" ? (
            <Breadcrumbs
              separator={<NavigateNextIcon fontSize="small" />}
              aria-label="breadcrumb"
              style={{ fontSize: "18px", fontWeight: "bold" }}
            >
              <Link color="primary" to="/crops">
                Crops
              </Link>
              <Link color="primary" to={`/crops`} state={{ crop_name: crop_name }}>{crop_name}</Link>
              <Link color="primary" to={`/crops/${crop_id}/${crop_name}/crop_config`}>{variety_name}</Link>
              <Link color="primary" to={`/crops/${crop_id}/${crop_name}/crop_config`}>{machine_name}</Link>
              <Link color="primary" to={`/crops/${crop_id}/${crop_name}/crop_config`}>
                Update Crop Configurations
              </Link>
            </Breadcrumbs>
          ) : (
            <Breadcrumbs
              separator={<NavigateNextIcon fontSize="small" />}
              aria-label="breadcrumb"
              style={{ fontSize: "18px", fontWeight: "bold" }}
            >
              <Link color="primary" to="/crops">
                Crops
              </Link>
              <Link color="primary" to={`/crops`}>{crop_name}</Link>
              <Link color="primary" to={`/crops/${crop_id}/${crop_name}/crop_config`}>
                Add Crop Configurations
              </Link>
            </Breadcrumbs>
          )}
        </div>
        {loading.fetch ? (
          <Loader color="primary" size={40} />
        ) : (
          <>
            <Formik
              initialValues={{
                varietyId: editConfig ? editConfig.variety_id : "",
                machineId: editConfig ? editConfig.machine_id : "",
                type: editConfig ? editConfig.type : "",
                pipeline_id: editConfig ? (editConfig?.response_config?.pipeline_id || editConfig?.crop_class_config?.pipeline_id) : "",
                contactMethod: editConfig
                  ? editConfig.request_config
                    ? "request_config"
                    : editConfig.crop_class_config
                      ? "crop_class_config"
                      : editConfig.response_config
                        ? "response_config"
                        : editConfig.crop_instruction_config
                          ? "crop_instruction_config"
                          : ""
                  : "",
                min_threshold: editConfig
                  ? editConfig?.crop_instruction_config?.min_threshold
                  : 0,
                mid_threshold: editConfig
                  ? editConfig?.crop_instruction_config?.mid_threshold
                  : 0,
                max_threshold: editConfig
                  ? editConfig?.crop_instruction_config?.max_threshold
                  : 0,
                paper_type: editConfig
                  ? editConfig?.crop_instruction_config?.paper_type
                  : "",
                processed_image: editConfig
                  ? editConfig?.response_config?.processed_image
                  : [],
                kernel_details: editConfig
                  ? editConfig?.response_config?.kernel_details
                  : [],
                kernel_distributions: editConfig
                  ? editConfig?.response_config?.kernel_distributions
                  : [],
                dist_avg_indexes: editConfig
                  ? editConfig?.response_config?.dist_avg_indexes
                  : [],
                alerts: editConfig ? editConfig?.response_config?.alerts : [],
                cropped_kernel_details: editConfig
                  ? editConfig?.response_config?.cropped_kernel_details
                  : [],
                consider_area: editConfig
                  ? editConfig?.crop_class_config?.consider_area
                  : false,
                consider_length: editConfig
                  ? editConfig?.crop_class_config?.consider_length
                  : false,
                consider_width: editConfig
                  ? editConfig?.crop_class_config?.consider_width
                  : false,
                show_segmented_image: editConfig
                  ? editConfig?.crop_class_config?.show_segmented_image
                  : false,
                distribution_avg_values: editConfig
                  ? editConfig?.crop_class_config?.distribution_avg_values
                  : [],
                distribution_key: editConfig
                  ? editConfig?.crop_class_config?.distribution_key
                  : [],
                class_order: editConfig
                  ? editConfig?.crop_class_config?.class_order
                  : [],
                display_order: editConfig
                  ? editConfig?.crop_class_config?.display_order
                  : [],
                class_mappings: editConfig
                  ? editConfig?.crop_class_config?.class_mappings
                  : {},
                quality_score_dependencies: editConfig
                  ? editConfig?.crop_class_config?.quality_score_dependencies
                  : [],
                grading: editConfig
                  ? editConfig?.crop_class_config?.grading
                  : {},
                request_config: editConfig?.request_config
                  ? editConfig.request_config
                  : [],
              }}
              validationSchema={validationSchema}
              onSubmit={confirmDialog}
              enableReinitialize
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                setFieldValue,
                handleSubmit,
              }) => {
                const rows = values.class_order?.map((className, index) => ({
                  id: index,
                  classOrder: className,
                  displayOrder: values.class_mappings[className] || "",
                }));
                const columns = [
                  {
                    field: "classOrder",
                    headerName: "Class Order",
                    headerClassName: "table-header",
                    disableColumnMenu: true,
                    flex: 1,
                  },
                  {
                    field: "displayOrder",
                    headerName: "Display Order",
                    headerClassName: "table-header",
                    disableColumnMenu: true,
                    flex: 1,
                    renderCell: (params: GridCellParams) => (
                      <Select
                        key={values.display_order.join(",")}
                        value={params.row.displayOrder || ""}
                        onChange={(event) => {
                          const selectedOption = event.target.value;
                          const newMapping: Record<string, string> = {
                            ...values.class_mappings,
                            [params.row.classOrder]: selectedOption,
                          };
                          setFieldValue("class_mappings", newMapping);
                        }}
                        name={`displayOrder-${params.row.id}`}
                        variant="standard"
                        displayEmpty
                        required
                        style={{ width: 200 }}
                      >
                        <MenuItem value="" disabled>
                          Select Display Order
                        </MenuItem>
                        {values.display_order.map(
                          (option: string, index: number) => (
                            <MenuItem key={index} value={option}>
                              {option}
                            </MenuItem>
                          )
                        )}
                      </Select>
                    ),
                  },
                ];
                const modifiedOptions =
                  values?.type === "DEFAULT_ENAM"
                    ? [...kernelDetailsOptions, "grading"]
                    : kernelDetailsOptions;
                return (
                  <>
                    {loading.update || loading.create ? (
                      <Loader color="primary" size={40} />
                    ) : (
                      <Form onSubmit={handleSubmit}>
                        <Grid container spacing={2}>
                          {action === "add" &&
                            varieties.length > 0 && machines.length > 0 && (
                              <Grid container spacing={2}>
                                <Grid size={4}>
                                  <Dropdown
                                    options={varieties.map((variety) => ({
                                      id: variety.variety_id,
                                      name: variety.variety_name,
                                    }))}
                                    value={values.varietyId ?? ""}
                                    onChange={(id) => setFieldValue("varietyId", id)}
                                    label="Variety"
                                    required
                                    error={Boolean(touched.varietyId && errors.varietyId)}
                                    helperText={
                                      touched.varietyId && errors.varietyId
                                        ? String(errors.varietyId)
                                        : undefined
                                    }
                                  />
                                </Grid>
                                <Grid size={4}>
                                  <Dropdown
                                    options={machines.map((machine) => ({
                                      id: machine.machine_id,
                                      name: machine.machine_name,
                                    }))}
                                    value={values.machineId ?? ""}
                                    onChange={(id) => setFieldValue("machineId", id)}
                                    label="Machine"
                                    required
                                    error={Boolean(touched.machineId && errors.machineId)}
                                    helperText={
                                      touched.machineId && errors.machineId
                                        ? String(errors.machineId)
                                        : undefined
                                    }
                                  />
                                </Grid>
                                <Grid size={4}>
                                  <Dropdown
                                    options={enamTypeValues}
                                    value={values.type}
                                    onChange={(option) => setFieldValue("type", option)}
                                    label="Flow Type"
                                    required
                                    error={touched.type && Boolean(errors.type)}
                                    helperText={touched.type && errors.type ? String(errors.type) : ""}
                                  />
                                </Grid>
                              </Grid>
                            )}
                          <Grid size={2}>
                            <TextField
                              id="pipeline_id"
                              name="pipeline_id"
                              label="Pipeline ID"
                              variant="outlined"
                              fullWidth
                              value={values.pipeline_id}
                              onChange={handleChange}
                              required
                              error={Boolean(touched.pipeline_id && errors.pipeline_id)}
                              helperText={touched.pipeline_id && errors.pipeline_id}
                            />
                          </Grid>
                          <>
                            <Grid size={12} sx={{ marginBottom: "20px" }}>
                              <FormControl
                                component="fieldset"
                                error={
                                  touched.contactMethod &&
                                  !!errors.contactMethod
                                }
                              >
                                <RadioGroup
                                  aria-label="contact-method"
                                  name="contactMethod"
                                  value={values.contactMethod}
                                  onChange={handleChange}
                                  row
                                >
                                  <FormControlLabel
                                    value="request_config"
                                    control={<Radio />}
                                    label="Request Config"
                                  />
                                  <FormControlLabel
                                    value="crop_class_config"
                                    control={<Radio />}
                                    label="Crop Class Config"
                                  />
                                  <FormControlLabel
                                    value="crop_instruction_config"
                                    control={<Radio />}
                                    label="Crop Instruction Config"
                                  />
                                  <FormControlLabel
                                    value="response_config"
                                    control={<Radio />}
                                    label="Response Config"
                                  />
                                </RadioGroup>
                                {touched.contactMethod &&
                                  errors.contactMethod && (
                                    <>
                                      <FormHelperText>
                                        {touched.contactMethod &&
                                          errors.contactMethod}
                                      </FormHelperText>
                                    </>
                                  )}
                              </FormControl>
                              <Tooltip title="Paste">
                                <ContentCopyRounded
                                  color="primary"
                                  style={{
                                    cursor: "pointer",
                                    marginTop: "10px",
                                  }}
                                  onClick={handlePasteClick}
                                />
                              </Tooltip>
                            </Grid>
                            {values.contactMethod ===
                              "crop_instruction_config" && (
                                <Grid container spacing={2}>
                                  <Grid size={3}>
                                    <TextField
                                      label="Min Threshold"
                                      type="number"
                                      value={values.min_threshold}
                                      onChange={handleChange}
                                      name="min_threshold"
                                      className="no-spin"
                                      error={
                                        touched.min_threshold &&
                                        !!errors.min_threshold
                                      }
                                      helperText={
                                        touched.min_threshold &&
                                        errors.min_threshold
                                      }
                                    />
                                  </Grid>
                                  <Grid size={3}>
                                    <TextField
                                      label="Mid Threshold"
                                      type="number"
                                      value={values.mid_threshold}
                                      onChange={handleChange}
                                      name="mid_threshold"
                                      className="no-spin"
                                      error={
                                        touched.mid_threshold &&
                                        !!errors.mid_threshold
                                      }
                                      helperText={
                                        touched.mid_threshold &&
                                        errors.mid_threshold
                                      }
                                    />
                                  </Grid>
                                  <Grid size={3}>
                                    <TextField
                                      label="Max Threshold"
                                      type="number"
                                      value={values.max_threshold}
                                      onChange={handleChange}
                                      name="max_threshold"
                                      className="no-spin"
                                      error={
                                        touched.max_threshold &&
                                        !!errors.max_threshold
                                      }
                                      helperText={
                                        touched.max_threshold &&
                                        errors.max_threshold
                                      }
                                    />
                                  </Grid>
                                  <Grid size={3}>
                                    <Dropdown
                                      options={["A5", "A4", "A3"]}
                                      value={values.paper_type}
                                      label="Paper Type"
                                      onChange={(option) => setFieldValue("paper_type", option)}
                                      required
                                      error={Boolean(touched.paper_type && errors.paper_type)}
                                      helperText={
                                        touched.paper_type && errors.paper_type
                                          ? String(errors.paper_type)
                                          : undefined
                                      }
                                    />
                                  </Grid>
                                </Grid>
                              )}
                            {values.contactMethod === "response_config" && (
                              <Grid container spacing={2} direction="column">
                                <Grid size={12}>
                                  <Dropdown
                                    options={processImageOptions}
                                    value={values.processed_image}
                                    onChange={(option) => setFieldValue("processed_image", option)}
                                    label="Process Image"
                                    error={touched.processed_image && Boolean(errors.processed_image)}
                                    helperText={
                                      touched.processed_image && errors.processed_image
                                        ? String(errors.processed_image)
                                        : ""
                                    }
                                    showSelectedOptions
                                    checkmark
                                  />
                                </Grid>
                                <Grid size={12}>
                                  <Dropdown
                                    options={modifiedOptions}
                                    value={values.kernel_details}
                                    onChange={(option) => setFieldValue("kernel_details", option)}
                                    label="Kernel Details"
                                    error={touched.kernel_details && Boolean(errors.kernel_details)}
                                    helperText={
                                      touched.kernel_details && errors.kernel_details
                                        ? String(errors.kernel_details)
                                        : ""
                                    }
                                    showSelectedOptions
                                    checkmark
                                  />
                                </Grid>
                                <Grid size={12}>
                                  <Dropdown
                                    options={kernelDistributionsOptions}
                                    value={values.kernel_distributions}
                                    onChange={(option) => setFieldValue("kernel_distributions", option)}
                                    label="Kernel Distributions"
                                    error={touched.kernel_distributions && Boolean(errors.kernel_distributions)}
                                    helperText={
                                      touched.kernel_distributions && errors.kernel_distributions
                                        ? String(errors.kernel_distributions)
                                        : ""
                                    }
                                    showSelectedOptions
                                    checkmark
                                  />
                                </Grid>
                                <Grid size={12}>
                                  <Dropdown
                                    options={distAvgIndexesOptions}
                                    value={values.dist_avg_indexes}
                                    onChange={(option) => setFieldValue("dist_avg_indexes", option)}
                                    label="Dist Avg Indexes"
                                    error={touched.dist_avg_indexes && Boolean(errors.dist_avg_indexes)}
                                    helperText={
                                      touched.dist_avg_indexes && errors.dist_avg_indexes
                                        ? String(errors.dist_avg_indexes)
                                        : ""
                                    }
                                    showSelectedOptions
                                    checkmark
                                  />
                                </Grid>
                                <Grid size={12}>
                                  <Dropdown
                                    options={alertsOptions}
                                    value={values.alerts}
                                    onChange={(option) => setFieldValue("alerts", option)}
                                    label="Alerts"
                                    error={touched.alerts && Boolean(errors.alerts)}
                                    helperText={
                                      touched.alerts && errors.alerts
                                        ? String(errors.alerts)
                                        : ""
                                    }
                                    showSelectedOptions
                                    checkmark
                                  />
                                </Grid>
                                <Grid size={12}>
                                  <Dropdown
                                    options={croppedKernelDetailsOptions}
                                    value={values.cropped_kernel_details}
                                    onChange={(option) => setFieldValue("cropped_kernel_details", option)}
                                    label="Cropped Kernel Details"
                                    error={touched.cropped_kernel_details && Boolean(errors.cropped_kernel_details)}
                                    helperText={
                                      touched.cropped_kernel_details && errors.cropped_kernel_details
                                        ? String(errors.cropped_kernel_details)
                                        : ""
                                    }
                                    showSelectedOptions
                                    checkmark
                                  />
                                </Grid>
                              </Grid>
                            )}
                            {values.contactMethod === "crop_class_config" && (
                              <>
                                <Grid container spacing={2} direction="column">
                                  <Grid size={12}>
                                    <Dropdown
                                      options={distributionAvgValuesOptions}
                                      value={values.distribution_avg_values}
                                      onChange={(option) => setFieldValue("distribution_avg_values", option)}
                                      label="Distribution Avg Values"
                                      error={
                                        touched.distribution_avg_values && Boolean(errors.distribution_avg_values)
                                      }
                                      helperText={
                                        touched.distribution_avg_values && errors.distribution_avg_values
                                          ? String(errors.distribution_avg_values)
                                          : ""
                                      }
                                      checkmark
                                      showSelectedOptions
                                    />
                                  </Grid>
                                  <Grid size={12}>
                                    <Dropdown
                                      options={distributionKeysOptions}
                                      value={values.distribution_key}
                                      onChange={(option) => setFieldValue("distribution_key", option)}
                                      label="Distribution Key"
                                      error={touched.distribution_key && Boolean(errors.distribution_key)}
                                      helperText={
                                        touched.distribution_key && errors.distribution_key
                                          ? String(errors.distribution_key)
                                          : ""
                                      }
                                      showSelectedOptions
                                      checkmark
                                    />
                                  </Grid>
                                </Grid>
                                <Grid size={12}>
                                  <FormControl component="fieldset">
                                    <FormGroup row sx={{ mt: 2, mb: 4 }}>
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            checked={values.consider_area}
                                            onChange={() =>
                                              setFieldValue(
                                                "consider_area",
                                                !values.consider_area
                                              )
                                            }
                                          />
                                        }
                                        label="Consider Area"
                                      />
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            checked={values.consider_length}
                                            onChange={() =>
                                              setFieldValue(
                                                "consider_length",
                                                !values.consider_length
                                              )
                                            }
                                          />
                                        }
                                        label="Consider Length"
                                      />
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            checked={values.consider_width}
                                            onChange={() =>
                                              setFieldValue(
                                                "consider_width",
                                                !values.consider_width
                                              )
                                            }
                                          />
                                        }
                                        label="Consider Width"
                                      />
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            checked={
                                              values.show_segmented_image
                                            }
                                            onChange={() =>
                                              setFieldValue(
                                                "show_segmented_image",
                                                !values.show_segmented_image
                                              )
                                            }
                                          />
                                        }
                                        label="show Segmented Image"
                                      />
                                    </FormGroup>
                                  </FormControl>
                                </Grid>
                                <Grid size={12}>
                                  <div>
                                    <strong>Class Order:</strong>{" "}
                                    {Array.isArray(values.class_order)
                                      ? values.class_order.join(", ")
                                      : values.class_order || "N/A"}
                                  </div>
                                </Grid>
                                <Grid size={12}>
                                  <div>
                                    <strong>Display Order:</strong>{" "}
                                    {Array.isArray(values.display_order)
                                      ? values.display_order.join(", ")
                                      : values.display_order || "N/A"}
                                  </div>
                                </Grid>
                                {classes.length > 0 && (
                                  <Grid
                                    container spacing={2} direction="row">
                                    <Grid size={4}>
                                      <Dropdown
                                        options={classes?.map((item) => ({
                                          id: item.class_name,
                                          name: item.class_name,
                                        }))}
                                        value={values.class_order ?? ""}
                                        onChange={(id) => {
                                          setFieldValue("class_order", id);
                                        }}
                                        label="Class Order"
                                        required
                                        error={Boolean(touched.class_order && errors.class_order)}
                                        helperText={
                                          touched.class_order && errors.class_order ? String(errors.class_order) : undefined
                                        }
                                        checkmark
                                      />
                                    </Grid>
                                    <Grid size={4}>
                                      <Dropdown
                                        options={classes?.map((item) => ({
                                          id: item.class_name,
                                          name: item.class_name,
                                        }))}
                                        value={values.display_order ?? ""}
                                        onChange={(selected) => {
                                          const selection = Array.isArray(selected) ? selected : [selected];
                                          setFieldValue("display_order", selection);
                                          const updatedDependencies = (values.quality_score_dependencies || []).filter((dep: string) =>
                                            selection.includes(dep)
                                          );
                                          setFieldValue("quality_score_dependencies", updatedDependencies);
                                        }}
                                        label="Display Order"
                                        required
                                        error={Boolean(touched.display_order && errors.display_order)}
                                        helperText={
                                          touched.display_order && errors.display_order
                                            ? String(errors.display_order)
                                            : undefined
                                        }
                                        checkmark
                                      />
                                    </Grid>
                                    {values?.display_order?.length > 0 && (
                                      <Grid size={4}>
                                        <Dropdown
                                          options={values?.display_order}
                                          value={values.quality_score_dependencies ?? []}
                                          onChange={(selected) => {
                                            const selection = Array.isArray(selected) ? selected : [selected];
                                            setFieldValue("quality_score_dependencies", selection);
                                          }}
                                          label="Quality Score Dependencies"
                                          required
                                          checkmark
                                          error={Boolean(
                                            touched.quality_score_dependencies && errors.quality_score_dependencies
                                          )}
                                          helperText={
                                            touched.quality_score_dependencies && errors.quality_score_dependencies
                                              ? Array.isArray(errors.quality_score_dependencies)
                                                ? errors.quality_score_dependencies.join(", ")
                                                : String(errors.quality_score_dependencies)
                                              : ""
                                          }
                                        />
                                      </Grid>
                                    )}
                                  </Grid>
                                )}
                                {values?.display_order?.length > 0 && (
                                  <>
                                    <Grid size={12} sx={{ mt: 2 }}>
                                      <strong>Class Mappings:</strong>
                                    </Grid>
                                    <Grid size={12}>
                                      <GridTable
                                        rows={rows}
                                        columns={columns}
                                        disableRowSelectionOnClick
                                      />
                                    </Grid>
                                    {values.type === "DEFAULT_ENAM" && (
                                      <>
                                        <Grid size={12} sx={{ mt: 4 }}>
                                          <strong>
                                            Enam Class Wise Range Config
                                          </strong>
                                        </Grid>
                                        <div
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "14px",
                                            marginTop: "20px"
                                          }}
                                        >
                                          <strong>
                                            Moisture Range
                                          </strong>
                                          <Grid size={3}>
                                            <Dropdown
                                              options={rangeTypeOptions}
                                              value={
                                                values?.grading?.moisture?.weightRange
                                                  ? "Weight"
                                                  : values?.grading?.moisture?.countRange
                                                    ? "Count"
                                                    : "Select By %"
                                              }
                                              onChange={(option) => {
                                                const gradingField = `grading['moisture']`;
                                                if (option === "Weight") {
                                                  setFieldValue(`${gradingField}.countRange`, undefined);
                                                  setFieldValue(`${gradingField}.weightRange`, []);
                                                } else if (option === "Count") {
                                                  setFieldValue(`${gradingField}.weightRange`, undefined);
                                                  setFieldValue(`${gradingField}.countRange`, []);
                                                } else {
                                                  setFieldValue(`${gradingField}.countRange`, undefined);
                                                  setFieldValue(`${gradingField}.weightRange`, undefined);
                                                }
                                              }}
                                              label="% By"
                                              required
                                            />
                                          </Grid>
                                          {[0, 1, 2].map((index) => {
                                            const gradingField = `grading['moisture']`;
                                            const rangeType = values?.grading?.moisture?.weightRange
                                              ? "Weight"
                                              : values?.grading?.moisture?.countRange
                                                ? "Count"
                                                : "Select By %";

                                            const value =
                                              rangeType === "Count"
                                                ? values?.grading?.moisture?.countRange?.[index] ?? ""
                                                : values?.grading?.moisture?.weightRange?.[index] ?? "";

                                            return (
                                              <TextField
                                                key={index}
                                                label={`Range ${index + 1}`}
                                                type="number"
                                                value={value}
                                                size="small"
                                                className="no-spin"
                                                onChange={(e) => {
                                                  const rawValue = e.target.value;
                                                  const parsedValue = rawValue === "" ? "" : Number(rawValue);
                                                  const targetField =
                                                    rangeType === "Count"
                                                      ? `${gradingField}.countRange[${index}]`
                                                      : `${gradingField}.weightRange[${index}]`;

                                                  if (rangeType !== "Select By %") {
                                                    setFieldValue(targetField, parsedValue);
                                                  }
                                                }}
                                              />
                                            );
                                          })}
                                        </div>
                                        <Grid size={12}>
                                          <GridTable
                                            rows={values.display_order.map(
                                              (displayOrderKey, index) => {
                                                const gradingItem =
                                                  values?.grading?.[
                                                  displayOrderKey
                                                  ] ?? {};
                                                const rangeType =
                                                  gradingItem.weightRange
                                                    ? "Weight"
                                                    : gradingItem.countRange
                                                      ? "Count"
                                                      : "Select By %";

                                                return {
                                                  id: index,
                                                  displayOrder: displayOrderKey,
                                                  rangeType,
                                                  range1:
                                                    gradingItem
                                                      .countRange?.[0] ??
                                                    gradingItem
                                                      .weightRange?.[0] ??
                                                    "",
                                                  range2:
                                                    gradingItem
                                                      .countRange?.[1] ??
                                                    gradingItem
                                                      .weightRange?.[1] ??
                                                    "",
                                                  range3:
                                                    gradingItem
                                                      .countRange?.[2] ??
                                                    gradingItem
                                                      .weightRange?.[2] ??
                                                    "",
                                                };
                                              }
                                            )}
                                            columns={[
                                              {
                                                field: "displayOrder",
                                                headerName: "Display Order",
                                                headerClassName: "table-header",
                                                flex: 1,
                                              },
                                              {
                                                field: "rangeType",
                                                headerName: "% By",
                                                headerClassName: "table-header",
                                                flex: 1,
                                                renderCell: (params) => (
                                                  <Dropdown
                                                    options={rangeTypeOptions}
                                                    value={params.row.rangeType}
                                                    onChange={(option) => {
                                                      const gradingField = `grading[${params.row.displayOrder}]`;

                                                      if (option === "Weight") {
                                                        setFieldValue(`${gradingField}.countRange`, undefined);
                                                        setFieldValue(`${gradingField}.weightRange`, []);
                                                      } else if (option === "Count") {
                                                        setFieldValue(`${gradingField}.weightRange`, undefined);
                                                        setFieldValue(`${gradingField}.countRange`, []);
                                                      } else {
                                                        setFieldValue(`${gradingField}.countRange`, undefined);
                                                        setFieldValue(`${gradingField}.weightRange`, undefined);
                                                      }
                                                    }}
                                                    label="% By"
                                                    variant="standard"
                                                    width={150}
                                                  />
                                                ),
                                              },
                                              {
                                                field: "range1",
                                                headerName: "Range 1",
                                                headerClassName: "table-header",
                                                flex: 1,
                                                renderCell: (params) => (
                                                  <TextField
                                                    label="Range 1"
                                                    type="number"
                                                    value={params.row.range1}
                                                    size="small"
                                                    variant="standard"
                                                    sx={{ width: "150px" }}
                                                    className="no-spin"
                                                    onChange={(e) => {
                                                      const rawValue = e.target.value;
                                                      const value = rawValue === "" ? "" : Number(rawValue);
                                                      const gradingField = `grading[${params.row.displayOrder}]`;
                                                      const targetField =
                                                        params.row.rangeType === "Count"
                                                          ? `${gradingField}.countRange[0]`
                                                          : `${gradingField}.weightRange[0]`;

                                                      if (params.row.rangeType !== "Select By %") {
                                                        setFieldValue(targetField, value);
                                                      }
                                                    }}
                                                  />
                                                ),
                                              },
                                              {
                                                field: "range2",
                                                headerName: "Range 2",
                                                headerClassName: "table-header",
                                                flex: 1,
                                                renderCell: (params) => (
                                                  <TextField
                                                    label="Range 2"
                                                    type="number"
                                                    value={params.row.range2}
                                                    size="small"
                                                    variant="standard"
                                                    sx={{ width: "150px" }}
                                                    className="no-spin"
                                                    onChange={(e) => {
                                                      const rawValue = e.target.value;
                                                      const value = rawValue === "" ? "" : Number(rawValue);
                                                      const gradingField = `grading[${params.row.displayOrder}]`;
                                                      const targetField =
                                                        params.row.rangeType === "Count"
                                                          ? `${gradingField}.countRange[1]`
                                                          : `${gradingField}.weightRange[1]`;

                                                      if (params.row.rangeType !== "Select By %") {
                                                        setFieldValue(targetField, value);
                                                      }
                                                    }}
                                                  />
                                                ),
                                              },
                                              {
                                                field: "range3",
                                                headerName: "Range 3",
                                                headerClassName: "table-header",
                                                flex: 1,
                                                renderCell: (params) => (
                                                  <TextField
                                                    label="Range 3"
                                                    type="number"
                                                    value={params.row.range3}
                                                    size="small"
                                                    variant="standard"
                                                    sx={{ width: "150px" }}
                                                    className="no-spin"
                                                    onChange={(e) => {
                                                      const rawValue = e.target.value;
                                                      const value = rawValue === "" ? "" : Number(rawValue);
                                                      const gradingField = `grading[${params.row.displayOrder}]`;
                                                      const targetField =
                                                        params.row.rangeType === "Count"
                                                          ? `${gradingField}.countRange[2]`
                                                          : `${gradingField}.weightRange[2]`;

                                                      if (params.row.rangeType !== "Select By %") {
                                                        setFieldValue(targetField, value);
                                                      }
                                                    }}
                                                  />
                                                ),
                                              },
                                            ]}
                                            disableRowSelectionOnClick
                                            density="standard"
                                          />
                                        </Grid>
                                      </>
                                    )}
                                  </>
                                )}
                              </>
                            )}
                            {values.contactMethod === "request_config" && (
                              <>
                                <Box
                                  style={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    width: "100%",
                                  }}
                                >
                                  <Button
                                    label="Add"
                                    variant="contained"
                                    color="primary"
                                    onClick={openDialog}
                                  />
                                </Box>
                                <Grid size={12} sx={{ mt: 1 }}>
                                  <GridTable
                                    rows={tableData.map((row, index) => ({
                                      ...row,
                                      sr_no: index + 1,
                                    }))}
                                    columns={req_columns}
                                    disableRowSelectionOnClick
                                    getRowId={(row) => row.sr_no}
                                  />
                                </Grid>
                              </>
                            )}
                            <Grid size={12}>
                              <Box display="flex" justifyContent="flex-end">
                                <Button
                                  type="submit"
                                  label={action === "edit" ? "Update" : "Add"}
                                  variant="contained"
                                />
                              </Box>
                            </Grid>
                          </>
                        </Grid>
                      </Form>
                    )}
                  </>
                );
              }}
            </Formik>
          </>
        )}
      </main>
      <AlertDialogModal
        open={isDialogOpen}
        onClose={() => closeDialog()}
        onConfirm={handleReqConfig}
        title={editMode ? "Edit Request Config" : "Add Request Config"}
        confirmButtonText={editMode ? "Update" : "Add"}
        cancelButtonText="Cancel"
        icon={<PermDataSettingIcon />}
        message={
          <Box padding={6}>
            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  name="display_name"
                  label="Display Name"
                  value={editRequestConfig.display_name}
                  onChange={(e) =>
                    handleInputChange("display_name", e.target.value)
                  }
                />
              </Grid>
              <Grid size={12}>
                {editRequestConfig.values.map((value, index) => (
                  <Chip
                    key={index}
                    label={value}
                    color="default"
                    sx={{ m: 1 }}
                    onDelete={() => handleDeleteValue(value)}
                  />
                ))}
                <Box display="flex" alignItems="center" gap={2}>
                  <TextField
                    name="values"
                    label="Values"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                  />
                  <Button
                    label="Add"
                    variant="contained"
                    color="primary"
                    onClick={handleAddValue}
                    disabled={!inputValue.trim()}
                  >
                    Add Value
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        }
      />
    </Box>
  );
};

export default ConfigDetails;
